import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  FiSearch,
  FiPlus,
  FiEdit,
  FiAlertCircle,
  FiCheckCircle,
} from "react-icons/fi";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { makeRequestAuth } from "../../_config/api";
import { showErrorToast, showSuccessToast } from "../../utils/toastUtils";
import Spinner from "../../utils/Loader";
import UpdateAppSettingModal from "./UpdateAppSettingModal";

function AllAppSettings() {
  const [settingsList, setSettingsList] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Modal States
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  // Status Action Modal
  const [statusModal, setStatusModal] = useState({
    open: false,
    id: null,
    isDisable: null,
  });

  const fetchSettings = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await makeRequestAuth(
        "get",
        "/appSetting/getAppSettingList",
      );
      // Adjust according to your response formatting
      const data = response?.data || [];
      setSettingsList(data);
    } catch (err) {
      showErrorToast(err.message || "Failed to fetch settings");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const openEditModal = (item) => {
    setSelectedRecord(item);
    setIsEditModalOpen(true);
  };

  const handleStatusToggle = async () => {
    const { id } = statusModal;
    try {
      await makeRequestAuth("post", "/appSetting/updateAppSettingSatus", {
        id,
      });

      setSettingsList((prev) =>
        prev.map((item) =>
          item._id === id ? { ...item, isDisable: !item.isDisable } : item,
        ),
      );
      showSuccessToast("Status updated successfully");
    } catch (err) {
      showErrorToast("Failed to update status");
    } finally {
      setStatusModal({ open: false, id: null, isDisable: null });
    }
  };

  // Search Filter
  const filteredList = settingsList.filter(
    (item) =>
      item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.key?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  if (isLoading && settingsList.length === 0) return <Spinner />;

  return (
    <div className="grid grid-cols-1 gap-6 p-4">
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="bg-white dark:bg-[#1E2738] text-gray-800 dark:text-white rounded-2xl p-6 md:p-8 border border-purple-300 dark:border-purple-600/50 shadow-xl">
        {/* HEADER */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div>
            <h2 className="text-xl md:text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              App Settings
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Manage core application configurations.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
            <div className="relative w-full sm:w-64">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search name or key..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 rounded-xl border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 text-gray-800 dark:text-white focus:ring-2 focus:ring-purple-500 outline-none transition"
              />
            </div>

            <Link to="/app-settings/create">
              <button className="flex items-center justify-center gap-2 w-full sm:w-auto bg-gradient-to-r from-blue-600 to-purple-600 text-white px-5 py-2 rounded-xl font-medium shadow-lg hover:scale-[1.02] transition">
                <FiPlus size={18} />
                <span>Add Setting</span>
              </button>
            </Link>
          </div>
        </div>

        {/* TABLE */}
        <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full table-auto text-left border-collapse">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-800/50 text-gray-600 dark:text-gray-300 text-sm uppercase tracking-wider font-semibold border-b border-gray-200 dark:border-gray-700">
                  <th className="py-4 px-6 w-16 text-center">#</th>
                  <th className="py-4 px-6">Name</th>
                  <th className="py-4 px-6">Key</th>
                  <th className="py-4 px-6">Value</th>
                  <th className="py-4 px-6 text-center">Status</th>
                  <th className="py-4 px-6 text-right">Actions</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {filteredList.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center py-10 text-gray-500">
                      No App Settings Found
                    </td>
                  </tr>
                ) : (
                  filteredList.map((item, index) => (
                    <tr
                      key={item._id}
                      className={`hover:bg-blue-50/50 dark:hover:bg-gray-700/30 transition ${item.isDisable ? "opacity-60" : ""}`}
                    >
                      <td className="py-4 px-6 text-center">{index + 1}</td>
                      <td className="py-4 px-6 font-medium capitalize">
                        {item.name}
                      </td>
                      <td className="py-4 px-6 font-mono text-sm text-gray-600 dark:text-gray-400">
                        {item.key || "-"}
                      </td>
                      <td className="py-4 px-6">
                        <div className="max-w-xs truncate text-gray-600 dark:text-gray-300">
                          {item.value || "-"}
                        </div>
                      </td>

                      <td className="py-4 px-6 text-center">
                        <span
                          className={`text-xs font-bold px-2 py-1 rounded ${item.isDisable ? "bg-red-100 text-red-600" : "bg-emerald-100 text-emerald-600"}`}
                        >
                          {item.isDisable ? "INACTIVE" : "ACTIVE"}
                        </span>
                      </td>

                      <td className="py-4 px-6 text-right">
                        <div className="flex justify-end items-center gap-4">
                          <button
                            onClick={() => openEditModal(item)}
                            className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 transition"
                            title="Edit"
                          >
                            <FiEdit size={18} />
                          </button>

                          {/* TOGGLE SWITCH */}
                          <button
                            onClick={() =>
                              setStatusModal({
                                open: true,
                                id: item._id,
                                isDisable: item.isDisable,
                              })
                            }
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${!item.isDisable ? "bg-emerald-500" : "bg-gray-300 dark:bg-gray-600"}`}
                          >
                            <span
                              className={`${!item.isDisable ? "translate-x-6" : "translate-x-1"} inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                            />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* STATUS CONFIRMATION MODAL */}
      {statusModal.open && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-50 p-4">
          <div className="bg-white dark:bg-[#1E2738] p-8 rounded-2xl shadow-2xl max-w-sm w-full text-center border border-gray-200 dark:border-gray-600 animate-fadeIn">
            <div
              className={`mx-auto flex items-center justify-center h-16 w-16 rounded-full mb-6 ${statusModal.isDisable ? "bg-emerald-100 text-emerald-600" : "bg-yellow-100 text-yellow-600"}`}
            >
              {statusModal.isDisable ? (
                <FiCheckCircle size={32} />
              ) : (
                <FiAlertCircle size={32} />
              )}
            </div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
              Update Status?
            </h3>
            <p className="text-gray-600 dark:text-gray-300 mb-8">
              Are you sure you want to{" "}
              {statusModal.isDisable ? "Activate" : "Deactivate"} this setting?
            </p>

            <div className="flex justify-center gap-3">
              <button
                onClick={() =>
                  setStatusModal({ open: false, id: null, isDisable: null })
                }
                className="flex-1 px-5 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium transition"
              >
                Cancel
              </button>
              <button
                onClick={handleStatusToggle}
                className={`flex-1 px-5 py-2.5 rounded-xl text-white font-medium shadow-lg transition ${statusModal.isDisable ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-500 hover:bg-red-600"}`}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* EDIT MODAL */}
      <UpdateAppSettingModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        fetchSettings={fetchSettings}
        initialData={selectedRecord}
      />
    </div>
  );
}

export default AllAppSettings;
